import React, {useMemo, useEffect} from 'react';
import {keyBy} from 'lodash';
import {Link} from 'react-router-dom';
import {useTable, usePagination} from 'react-table';
import {Table, Button, Dropdown, Input, Icon} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {invoicesPageSizes, dealerListMaintain} from '../../../common/constants/lists';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {reverseShortDate} from '../../../common/constants/datesFormat';
import {convertDateToSpecificFormat} from '../../../common/util/date';

const DEALERS = keyBy(dealerListMaintain, 'value');

const validateUndefined = (dealer, value) => {
  if (typeof dealer !== 'undefined') {
    return dealer.text;
  }
  return value;
};

const columnsTableWithProps = (statusList, permissions, dealer) => {
  const isAdmin = hasAccessPermission(permissions, resources.ProcessedWarranty, scopes.See);

  return [
    {
      'Header': 'NO.TWC',
      'accessor': 'code',
    },
    {
      'Header': 'CONCESIONARIO',
      'accessor': 'dealerId',
      'Cell': ({row}) => {
        const {dealerId} = row.original;
        return validateUndefined(DEALERS[dealerId], dealerId);
      },
    },
    {
      'Header': 'NO.TWC (CONCESIONARIO)',
      'accessor': 'numTwcDealer',
    },
    {
      'Header': 'ORD/REP',
      'accessor': 'orderRepairId',
    },
    {
      'Header': 'CHASIS',
      'accessor': 'chassis',
    },
    {
      'Header': 'DESCRIPCION',
      'accessor': 'condition',
    },
    {
      'Header': 'FECHA INGRESO',
      'accessor': 'dateTwc',
      'Cell': ({row}) => {
        const {dateTwc} = row.original;
        return convertDateToSpecificFormat(dateTwc, reverseShortDate);
      },
    },
    {
      'Header': 'ESTADO',
      'accessor': 'stateTwc',
      'Cell': ({row}) => {
        const {stateTwc} = row.original;
        return statusList[stateTwc].description;
      },
    },
    {
      'Header': 'TOTAL GARANTÍA',
      'accessor': 'totalTwc',
    },
    {
      'Header': 'CAMPAÑA',
      'accessor': 'campaign',
    },
    {
      'Header': 'VER',
      'accessor': 'viewTwc',
      'width': 100,
      'Cell': ({row}) => {
        const {code, numTwcDealer, dealerId} = row.original;
        const path = isAdmin ?
          `/editar-garantias-admin/${code}/${numTwcDealer}/${dealerId}` :
          `/editar-garantias/${code}/${numTwcDealer}/${dealerId}`;
        const enableIcon = isAdmin ? 'false' : dealerId !== dealer ? 'true' : 'false';
        if (enableIcon === 'false') {
          return (
            <Link
              to={path}
              target='_blank'
            >
              <Icon name='eye' size='large'/>
            </Link>
          );
        }
        return (
          <Link
            to={path}
            target='_blank'
          >
            <Icon name='eye' size='large' disabled />
          </Link>
        );
      },
    },
  ];
};

const QueryTable = ({warranties, statusList, handleChange, filteredRows, permissions, dealer}) => {
  const columnsTable = columnsTableWithProps(statusList, permissions, dealer);
  const data = useMemo(() => warranties, []);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'initialState': {'pageIndex': 0, 'pageSize': 10},
    },
    usePagination
  );

  useEffect(() => {
    if (rows.length !== filteredRows.length) {
      handleChange('filteredRows', rows);
    }
  }, [rows]);

  return (
    <Table
      className='interactive'
      compact
      striped
      {...getTableProps()}
    >
      <Table.Header>
        {headerGroups.map((headerGroup) => (
          <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, index) => (
              <Table.HeaderCell
                key={`title-${index}`}
                textAlign='center'
                {...column.getHeaderProps()}
              >
                {column.render('Header')}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        ))}
      </Table.Header>
      <Table.Body {...getTableBodyProps()}>
        {!page.length && (
          <Table.Row>
            <Table.Cell colSpan={12} textAlign='center'>
              No hay datos disponibles
            </Table.Cell>
          </Table.Row>
        )}
        {page.length > 0 &&
          page.map((row, i) => {
            prepareRow(row);
            return (
              <Table.Row key={i} {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <Table.Cell textAlign='center' {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
      </Table.Body>
      {page.length > 0 && (
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={2} textAlign='left'>
              <Button
                className='navigation-button'
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                content='Anterior'
              />
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} textAlign='right'>
              <span>páginas </span>
              <span>
                <Input
                  className='pages-input'
                  type='number'
                  value={pageIndex + 1}
                  min='1'
                  max={pageOptions.length}
                  onChange={(e, {value}) => {
                    const page = value ? Number(value) - 1 : 0;
                    if (page < 1 || page > pageOptions.length) {
                      e.target.value = 1;
                    }
                    gotoPage(page);
                  }}
                />
              </span>{' '}
              <span>de {pageOptions.length}</span>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} textAlign='left'>
              <Dropdown
                className='page-sizes-selection'
                selection
                upward
                compact
                value={pageSize}
                options={invoicesPageSizes}
                onChange={(e, {value}) => {
                  setPageSize(Number(value));
                }}
              />
              <span>filas</span>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} textAlign='right'>
              <Button
                className='navigation-button'
                onClick={() => nextPage()}
                disabled={!canNextPage}
                content='Siguiente'
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );
};

export default QueryTable;
